/**
 * PriceTag — encapsulates the recurring "₹399  ~~₹499~~  20% OFF" pattern.
 *
 * Pass either:
 *   - `price` + (optional) `mrp`  — discount is auto-computed
 *   - `price` + `mrp` + `discountPercent` — explicit override (e.g. when the
 *     server provides `effective_discount_percent`)
 *
 *  Currency: ₹ by default. Pass `currency="$"` for other markets.
 */
import React from 'react';
import { css } from './tokens';

export interface PriceTagProps {
  price: number;
  mrp?: number | null;
  discountPercent?: number | null;
  size?: 'sm' | 'md' | 'lg';
  /** Show the discount pill inline. Defaults to true when there's a discount. */
  showDiscount?: boolean;
  /** Decimal places for the displayed price. Defaults to 0. */
  decimals?: number;
  currency?: string;
  align?: 'start' | 'end' | 'center';
  /** Stack price + MRP vertically (compact card mode). Defaults to inline. */
  stacked?: boolean;
}

const SIZE: Record<NonNullable<PriceTagProps['size']>, { price: number; mrp: number; pill: number }> = {
  sm: { price: 14, mrp: 11, pill: 10 },
  md: { price: 17, mrp: 12, pill: 11 },
  lg: { price: 22, mrp: 14, pill: 12 },
};

export const PriceTag: React.FC<PriceTagProps> = ({
  price,
  mrp,
  discountPercent,
  size = 'md',
  showDiscount,
  decimals = 0,
  currency = '₹',
  align = 'start',
  stacked = false,
}) => {
  // Resolve the displayed MRP & discount. Don't trust callers blindly — only
  // render the strikethrough MRP if it's actually greater than the price.
  const validMrp = mrp != null && mrp > price ? mrp : null;
  const computedPct =
    discountPercent != null
      ? Math.round(discountPercent)
      : validMrp
        ? Math.round(((validMrp - price) / validMrp) * 100)
        : 0;
  const showPill = (showDiscount ?? computedPct > 0) && computedPct > 0;

  const sizing = SIZE[size];

  const fmt = (n: number) =>
    decimals === 0
      ? `${currency}${Math.round(n).toLocaleString('en-IN')}`
      : `${currency}${n.toFixed(decimals)}`;

  return (
    <span
      style={{
        display: 'inline-flex',
        flexDirection: stacked ? 'column' : 'row',
        alignItems: stacked ? (align === 'end' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start') : 'baseline',
        gap: stacked ? 2 : 8,
        justifyContent: align === 'end' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start',
      }}
    >
      <span
        style={{
          fontSize: sizing.price,
          fontWeight: 800,
          color: css.color.text,
          letterSpacing: '-0.01em',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {fmt(price)}
      </span>
      {validMrp != null && (
        <span
          style={{
            fontSize: sizing.mrp,
            color: css.color.textMuted,
            textDecoration: 'line-through',
            fontWeight: 500,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {fmt(validMrp)}
        </span>
      )}
      {showPill && (
        <span
          style={{
            fontSize: sizing.pill,
            fontWeight: 800,
            color: '#FFFFFF',
            background: `linear-gradient(135deg, ${css.color.accent}, ${css.color.accentStrong})`,
            padding: '2px 6px',
            borderRadius: 4,
            letterSpacing: '0.02em',
            whiteSpace: 'nowrap',
          }}
        >
          {computedPct}% OFF
        </span>
      )}
    </span>
  );
};

export default PriceTag;

